import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getSession } from '../game/model/sessionHost'
import { useGameStore } from '../store/gameStore'
import {
  initialTutorialState,
  pickEnemyIntro,
  pickMechanicHint,
  reduceTutorial,
  tutorialViewModel,
  type TutorialEvent
} from './tutorialMachine'
import {
  tutorialStorage,
  type TutorialPersistedState
} from './tutorialStorage'

/**
 * Match-only contextual tutorial. Disabled during editor playtest.
 * Persistence is UI/local — never written into GameSession.
 */
export function useMatchTutorial() {
  const playtestMap = useGameStore((s) => s.playtestMap)
  const gameState = useGameStore((s) => s.gameState)
  const sessionView = useGameStore((s) => s.sessionView)
  const isPlaytest = playtestMap !== null
  const enabled = gameState === 'playing' && !isPlaytest

  const [state, setState] = useState<TutorialPersistedState>(() =>
    initialTutorialState(tutorialStorage.load())
  )

  const prev = useRef({
    towerCount: 0,
    matchState: '',
    wavesCompleted: -1,
    begun: false
  })

  const dispatch = useCallback((event: TutorialEvent) => {
    setState((current) => {
      const next = reduceTutorial(current, event)
      if (
        next.status !== current.status ||
        next.step !== current.step ||
        next.seenEnemyFamilies.length !== current.seenEnemyFamilies.length ||
        (next.seenMechanicHints?.length ?? 0) !== (current.seenMechanicHints?.length ?? 0)
      ) {
        tutorialStorage.save(next)
      }
      return next
    })
  }, [])

  useEffect(() => {
    if (!enabled) {
      prev.current.begun = false
      return
    }
    if (prev.current.begun) return
    prev.current.begun = true
    prev.current.towerCount = 0
    prev.current.matchState = ''
    prev.current.wavesCompleted = -1
    dispatch({ type: 'MATCH_BEGIN' })
  }, [enabled, dispatch])

  useEffect(() => {
    if (!enabled || !sessionView) return

    if (sessionView.towerCount > prev.current.towerCount) {
      dispatch({ type: 'TOWER_PLACED' })
      const session = getSession()
      if (session?.towers.some((t) => t.definitionId === 'frost' || t.definitionId === 'poison')) {
        dispatch({ type: 'CONTROL_TOWER_PLACED' })
      }
    }
    prev.current.towerCount = sessionView.towerCount

    if (sessionView.state === 'PLAYING' && prev.current.matchState !== 'PLAYING') {
      dispatch({ type: 'WAVE_STARTED' })
    }
    if (
      sessionView.state === 'READY' &&
      prev.current.matchState === 'PLAYING' &&
      sessionView.wavesCompleted > Math.max(0, prev.current.wavesCompleted)
    ) {
      dispatch({ type: 'WAVE_READY' })
    }
    prev.current.matchState = sessionView.state
    prev.current.wavesCompleted = sessionView.wavesCompleted

    // Skip Frost/Poison control hint on stages that lack those towers.
    if (state.step === 'controlHint') {
      const session = getSession()
      const tray = session?.listAvailableTowers().map((t) => t.id) ?? []
      if (!tray.includes('frost') && !tray.includes('poison')) {
        dispatch({ type: 'DISMISS_INFO' })
      }
    }
  }, [enabled, sessionView, dispatch, state.step])

  useEffect(() => {
    const onUpgraded = () => dispatch({ type: 'TOWER_UPGRADED' })
    const onTargeting = () => dispatch({ type: 'TARGETING_CHANGED' })
    window.addEventListener('shapes-td:tutorial-upgraded', onUpgraded)
    window.addEventListener('shapes-td:tutorial-targeting', onTargeting)
    return () => {
      window.removeEventListener('shapes-td:tutorial-upgraded', onUpgraded)
      window.removeEventListener('shapes-td:tutorial-targeting', onTargeting)
    }
  }, [dispatch])

  const enemyFamily = useMemo(() => {
    if (!enabled || !sessionView?.wavePreview) return null
    return pickEnemyIntro(
      sessionView.wavePreview.chips.map((c) => c.id),
      state.seenEnemyFamilies
    )
  }, [enabled, sessionView, state.seenEnemyFamilies])

  const mechanicId = useMemo(() => {
    if (!enabled || !sessionView?.wavePreview) return null
    const session = getSession()
    const allowed = session?.campaignContext?.allowedTowerIds
    return pickMechanicHint({
      allowedTowerIds: allowed,
      chipIds: sessionView.wavePreview.chips.map((c) => c.id),
      seen: state.seenMechanicHints ?? []
    })
  }, [enabled, sessionView, state.seenMechanicHints])

  useEffect(() => {
    if (!enabled || !enemyFamily || sessionView?.state !== 'PLAYING') return
    dispatch({ type: 'MARK_ENEMY_SEEN', family: enemyFamily })
  }, [enabled, enemyFamily, sessionView?.state, dispatch])

  const view = useMemo(
    () => tutorialViewModel(state, enabled ? enemyFamily : null, enabled ? mechanicId : null),
    [state, enabled, enemyFamily, mechanicId]
  )

  return {
    enabled,
    view,
    skip: useCallback(() => dispatch({ type: 'SKIP' }), [dispatch]),
    dismissInfo: useCallback(() => dispatch({ type: 'DISMISS_INFO' }), [dispatch]),
    acknowledgeEnemy: useCallback(() => {
      if (enemyFamily) dispatch({ type: 'MARK_ENEMY_SEEN', family: enemyFamily })
    }, [dispatch, enemyFamily]),
    acknowledgeMechanic: useCallback(() => {
      if (mechanicId) dispatch({ type: 'MARK_MECHANIC_SEEN', mechanic: mechanicId })
    }, [dispatch, mechanicId]),
    resetForReplay: useCallback(() => {
      const next = reduceTutorial(state, { type: 'REPLAY' })
      tutorialStorage.save(next)
      setState(next)
    }, [state])
  }
}

export function emitTutorialUpgraded(): void {
  window.dispatchEvent(new Event('shapes-td:tutorial-upgraded'))
}

export function emitTutorialTargeting(): void {
  window.dispatchEvent(new Event('shapes-td:tutorial-targeting'))
}
